/**
 * BinaryViewer Component
 *
 * Displays information about binary files that cannot be previewed.
 * Provides download functionality for the file.
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { downloadWorkspaceFile } from '@/lib/downloadFile';
import {
  Download,
  FileArchive,
  FileAudio,
  FileVideo,
  File,
  HardDrive,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface BinaryViewerProps {
  /** Path to the file */
  path: string;
  /** File name (for display) */
  filename: string;
  /** File size in bytes */
  size: number;
  /** Additional CSS classes */
  className?: string;
}

// ============================================
// File Type Detection
// ============================================

interface FileTypeInfo {
  icon: React.ReactNode;
  label: string;
  description: string;
}

function getFileTypeInfo(filename: string): FileTypeInfo {
  const ext = filename.toLowerCase().split('.').pop() || '';

  // Archive files
  const archiveExts = ['zip', 'tar', 'gz', 'bz2', 'xz', '7z', 'rar', 'tgz'];
  if (archiveExts.includes(ext)) {
    return {
      icon: <FileArchive className="h-16 w-16" />,
      label: 'Archive File',
      description: 'Compressed archive containing multiple files',
    };
  }

  // Audio files
  const audioExts = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma'];
  if (audioExts.includes(ext)) {
    return {
      icon: <FileAudio className="h-16 w-16" />,
      label: 'Audio File',
      description: 'Audio content that cannot be previewed in the browser',
    };
  }

  // Video files
  const videoExts = ['mp4', 'webm', 'avi', 'mov', 'mkv', 'wmv', 'flv'];
  if (videoExts.includes(ext)) {
    return {
      icon: <FileVideo className="h-16 w-16" />,
      label: 'Video File',
      description: 'Video content that cannot be previewed in the browser',
    };
  }

  // Executable/binary files
  const execExts = ['exe', 'dll', 'so', 'dylib', 'bin', 'app'];
  if (execExts.includes(ext)) {
    return {
      icon: <HardDrive className="h-16 w-16" />,
      label: 'Binary Executable',
      description: 'Executable or library file',
    };
  }

  // Office documents (if not handled elsewhere)
  const officeExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'];
  if (officeExts.includes(ext)) {
    return {
      icon: <File className="h-16 w-16" />,
      label: 'Office Document',
      description: 'Document file that requires a specialized viewer',
    };
  }

  // Default
  return {
    icon: <File className="h-16 w-16" />,
    label: 'Binary File',
    description: 'This file type cannot be previewed in the browser',
  };
}

/**
 * Format file size in human-readable form
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// ============================================
// BinaryViewer Component
// ============================================

export function BinaryViewer({ path, filename, size, className }: BinaryViewerProps) {
  const fileTypeInfo = useMemo(() => getFileTypeInfo(filename), [filename]);

  const handleDownload = () => {
    void downloadWorkspaceFile(path, filename);
  };

  return (
    <div
      className={cn(
        'h-full w-full flex flex-col items-center justify-center',
        'bg-surface-50 dark:bg-surface-950',
        'p-8',
        className
      )}
    >
      <div className="text-center max-w-md">
        {/* Icon */}
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-surface-200 dark:bg-surface-800 text-surface-500 dark:text-surface-400 mb-6">
          {fileTypeInfo.icon}
        </div>

        {/* File name */}
        <h2 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-2 break-all">
          {filename}
        </h2>

        {/* File type label */}
        <p className="text-surface-600 dark:text-surface-400 mb-1">
          {fileTypeInfo.label}
        </p>

        {/* File size */}
        {size > 0 && (
          <p className="text-sm text-surface-500 dark:text-surface-500 mb-4">
            {formatFileSize(size)}
          </p>
        )}

        {/* Description */}
        <p className="text-sm text-surface-500 dark:text-surface-500 mb-6">
          {fileTypeInfo.description}
        </p>

        {/* Download button */}
        <Button onClick={handleDownload} className="gap-2">
          <Download className="h-4 w-4" />
          Download File
        </Button>
      </div>
    </div>
  );
}

export default BinaryViewer;
